import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { WorkGroupBrowserToolbar } from '@/features/workgroups/components/work-group-browser-toolbar';
import { WorkGroupContentsList } from '@/features/workgroups/components/work-group-contents-list';
import { PrivateAccessReasonDialog } from '@/features/workgroups/components/private-access-reason-dialog';
import { WorkGroupFolderPermissionsDialog } from '@/features/workgroups/components/work-group-folder-permissions-dialog';
import { WorkGroupMembersPanel } from '@/features/workgroups/components/work-group-members-panel';
import { WorkGroupPrivateAccessBanner } from '@/features/workgroups/components/work-group-private-access-banner';
import { WorkGroupSettingsDialog } from '@/features/workgroups/components/work-group-settings-dialog';
import { usePermissions } from '@/hooks/use-permissions';
import {
  useWorkGroup,
  useWorkGroupContents,
  useWorkgroupsPrivateAccess,
} from '@/hooks/use-workgroups';
import {
  useWorkgroupsUIStore,
  WORKGROUPS_PRIVATE_REASON_MIN_LEN,
} from '@/stores/workgroups-ui-store';

export function WorkGroupsDetailPage() {
  const { workGroupId } = useParams<{ workGroupId: string }>();
  const { hasCapability } = usePermissions();
  const setSelectedWorkGroupId = useWorkgroupsUIStore((s) => s.setSelectedWorkGroupId);
  const setSelectedFolderId = useWorkgroupsUIStore((s) => s.setSelectedFolderId);

  const privateAccessReason = useWorkgroupsUIStore((s) => {
    if (!workGroupId) return undefined;
    const trimmed = s.privateAccessByWorkGroupId[workGroupId]?.trim() ?? '';
    return trimmed.length >= WORKGROUPS_PRIVATE_REASON_MIN_LEN ? trimmed : undefined;
  });

  const { data: wg, isLoading: wgLoading, error: wgError } = useWorkGroup(
    workGroupId,
    privateAccessReason,
  );
  const access = useWorkgroupsPrivateAccess(workGroupId, wg);

  const forbiddenNeedsReason =
    axios.isAxiosError(wgError) &&
    wgError.response?.status === 403 &&
    access.canPrivateAdmin &&
    !privateAccessReason;

  const blockContent = access.reasonMissing || forbiddenNeedsReason;

  const { data: contents, isLoading: contentsLoading, error: contentsError } = useWorkGroupContents(
    workGroupId,
    undefined,
    undefined,
    privateAccessReason,
    !!workGroupId && !blockContent,
  );

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [permissionsFolder, setPermissionsFolder] = useState<{ id: string; name: string } | null>(
    null,
  );

  useEffect(() => {
    if (workGroupId) {
      setSelectedWorkGroupId(workGroupId);
      setSelectedFolderId(null);
    }
    return () => {
      setSelectedWorkGroupId(null);
      setSelectedFolderId(null);
    };
  }, [workGroupId, setSelectedWorkGroupId, setSelectedFolderId]);

  if (!workGroupId) {
    return <p className="text-sm text-muted-foreground">Missing work group.</p>;
  }

  const title = wg?.name?.trim() || 'Work group';
  const showTrashLink = hasCapability('workgroups.trash.view');
  const canEdit = hasCapability('workgroups.edit');
  const canDelete = hasCapability('workgroups.delete');
  const showSettings = canEdit || canDelete;
  const showMembersPanel = wg?.type === 'standalone' && hasCapability('workgroups.members.manage');

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <PrivateAccessReasonDialog
        open={access.promptOpen || forbiddenNeedsReason}
        onSubmit={access.submitReason}
      />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
          <p className="text-sm text-muted-foreground">
            Type: {wg?.type ?? '…'}
            {wg?.isArchived ? ' · Archived' : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {showTrashLink ? (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/workgroups/${workGroupId}/trash`}>Trash</Link>
            </Button>
          ) : null}
          {showSettings && wg ? (
            <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}>
              Settings
            </Button>
          ) : null}
        </div>
      </div>

      {wg ? (
        <WorkGroupSettingsDialog
          workGroupId={workGroupId}
          workGroup={wg}
          privateAccessReason={privateAccessReason}
          canEdit={canEdit}
          canDelete={canDelete}
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
        />
      ) : null}

      {access.needsReason && privateAccessReason ? (
        <WorkGroupPrivateAccessBanner
          reason={privateAccessReason}
          onChangeReason={() => access.clearReason()}
        />
      ) : null}

      {wgLoading ? <p className="text-sm text-muted-foreground">Loading work group…</p> : null}
      {wgError && !forbiddenNeedsReason ? (
        <p className="text-sm text-destructive">Could not load work group.</p>
      ) : null}
      {forbiddenNeedsReason ? (
        <p className="text-sm text-muted-foreground">
          Administrator access to this private work group requires a reason.
        </p>
      ) : null}

      {!blockContent ? (
        <>
          {showMembersPanel ? <WorkGroupMembersPanel workGroupId={workGroupId} /> : null}
          <WorkGroupBrowserToolbar
            workGroupId={workGroupId}
            currentFolderId={contents?.folder?.id}
            isRootFolder={contents?.folder?.isRoot ?? true}
            privateAccessReason={privateAccessReason}
            onManagePermissions={() => {
              if (contents?.folder) {
                setPermissionsFolder({ id: contents.folder.id, name: contents.folder.name });
              }
            }}
          />
          <section className="space-y-2">
            <h2 className="text-sm font-medium text-foreground">Root contents</h2>
            {contentsLoading ? (
              <p className="text-sm text-muted-foreground">Loading contents…</p>
            ) : null}
            {contentsError ? (
              <p className="text-sm text-destructive">Could not load folder contents.</p>
            ) : null}
            {contents ? (
              <WorkGroupContentsList
                workGroupId={workGroupId}
                data={contents}
                privateAccessReason={privateAccessReason}
                onManageFolderPermissions={(folder) =>
                  setPermissionsFolder({ id: folder.id, name: folder.name })
                }
              />
            ) : null}
          </section>
          {permissionsFolder ? (
            <WorkGroupFolderPermissionsDialog
              workGroupId={workGroupId}
              folderId={permissionsFolder.id}
              folderName={permissionsFolder.name}
              privateAccessReason={privateAccessReason}
              open={!!permissionsFolder}
              onOpenChange={(open) => {
                if (!open) setPermissionsFolder(null);
              }}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
